/**
 * DriverExplainer — the collapsed-by-default "what is this number?" panel for
 * the wizard's two judgment-heavy rate stages (tp-wizard PR-B task 12):
 *  - `wacc`: the discount rate as a RETURN HURDLE — what demanding 8.5% vs
 *    10.5% a year does to every discounted year (small moves swing the
 *    target materially);
 *  - `terminal_growth`: 永續成長 vs GDP — a forever-growth rate cannot
 *    outpace the economy it lives in, and because it compounds forever the
 *    small numbers matter.
 *
 * Collapsed on mount; the toggle (testID `tp-explainer-<which>-toggle`)
 * flips the localized paragraph. Copy lives in the chat.targetPrice.*
 * i18n keys (explainerTitle.wacc/.terminal + waccExplainer/terminalExplainer,
 * all three locales) — the fallbacks here are the en strings, same rule as
 * every chat component. Mounted by TargetPriceInputGroup at the marked slot
 * for exactly these two stages; the panel keeps its own theme-sourced styles
 * (the getTpRowStyles singleton rule is for row geometry, which this is not).
 */
import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

export type DriverExplainerWhich = 'wacc' | 'terminal_growth';

// Per-stage copy keys + en fallbacks — the SAME keys the three locales carry.
const COPY: Record<
  DriverExplainerWhich,
  { titleKey: string; titleFallback: string; bodyKey: string; bodyFallback: string }
> = {
  wacc: {
    titleKey: 'chat.targetPrice.explainerTitle.wacc',
    // PR-review fix D: the title carries the ROW'S vocabulary ("WACC %", the
    // chat.targetPrice.wacc label) so the bubble never names the same number
    // two ways one inch apart. Body copy unchanged.
    titleFallback: 'What is WACC %?',
    bodyKey: 'chat.targetPrice.waccExplainer',
    bodyFallback:
      'WACC is the annual return you demand for holding the stock — the hurdle rate the company must clear to create value. Discounting the same cash flows at 8.5% instead of 10.5% values every future year noticeably higher, so small changes here swing the target price materially.',
  },
  terminal_growth: {
    titleKey: 'chat.targetPrice.explainerTitle.terminal',
    titleFallback: 'What is terminal growth?',
    bodyKey: 'chat.targetPrice.terminalExplainer',
    bodyFallback:
      'Terminal growth is the rate profit grows at forever, after the forecast window ends. A forever-growth rate cannot outpace the economy it lives in, so it stays near long-run GDP (roughly 0–3%). Because it compounds forever, even a 1% change moves the answer a lot.',
  },
};

export function DriverExplainer({ which }: { which: DriverExplainerWhich }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const copy = COPY[which];
  return (
    <View style={styles.panel}>
      <Pressable
        testID={`tp-explainer-${which}-toggle`}
        style={styles.toggle}
        onPress={() => setOpen((o) => !o)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={t(copy.titleKey, { defaultValue: copy.titleFallback })}
      >
        <Text style={styles.toggleText}>
          {open ? '▾' : '▸'} {t(copy.titleKey, { defaultValue: copy.titleFallback })}
        </Text>
      </Pressable>
      {open && (
        <Text testID={`tp-explainer-${which}-body`} style={styles.body}>
          {t(copy.bodyKey, { defaultValue: copy.bodyFallback })}
        </Text>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    panel: {
      gap: theme.spacing.xxs,
    },
    toggle: {
      paddingVertical: theme.spacing.xxs,
    },
    toggleText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
    },
    body: {
      fontSize: theme.typography.fontSizeXs,
      lineHeight: theme.typography.fontSizeXs * 1.5,
      color: theme.colors.textTertiary,
    },
  });
}
